import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { NumberConverter } from '../../../inputConverter';
import { BaseComponent } from '../../base.component';

import { SliderMode } from './slider-types';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-slider-ng',
    templateUrl: './slider.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './slider.component.scss']
})
export class SliderComponent extends BaseComponent implements OnInit {
    private _description: string = '';
    private _field: string;
    private _gap: number = 0;
    private _max: number;
    private _maxValue: number;
    private _min: number;
    private _minValue: number = 0;
    private _mode: string = SliderMode.Single;
    private _step: number;
    private sliderRef;
    private sliderMaxRef;
    private sliderMinRef;

    @Input()
    set description(value) {
        this._description = value;
    }
    get description() {
        return this._description;
    }

    @Input()
    set field(value) {
        this._field = value;
    }
    get field() {
        return this._field;
    }

    @Input()
    set gap(value: number) {
        this._gap = NumberConverter(value);
    }
    get gap(): number {
        return this._gap;
    }

    @Input()
    set max(value: number) {
        this._max = NumberConverter(value);
    }
    get max(): number {
        return this._max;
    }

    @Input()
    set maxValue(value: number) {
        this._maxValue = NumberConverter(value);
        this.onMinMaxValueChange(false);
    }
    get maxValue(): number {
        return this._maxValue;
    }

    @Input()
    set min(value: number) {
        this._min = NumberConverter(value);
    }
    get min(): number {
        return this._min;
    }

    @Input()
    set minValue(value: number) {
        this._minValue = NumberConverter(value);
        this.onMinMaxValueChange(true);
    }
    get minValue(): number {
        return this._minValue;
    }

    @Input()
    set mode(value) {
        this._mode = value;
    }
    get mode() {
        return this._mode;
    }

    @Input()
    set step(value: number) {
        this._step = NumberConverter(value);
    }
    get step(): number {
        return this._step;
    }

    @Input()
    set value(value: number) {
        this.maxValue = NumberConverter(value);
    }
    get value(): number {
        return this.maxValue;
    }

    @ViewChild('slider') set slider(elementRef: ElementRef<HTMLDivElement>) {
        if (elementRef && !this.sliderRef) {
            this.sliderRef = elementRef;
        }
    }

    @ViewChild('sliderMax') set sliderMax(elementRef: ElementRef<HTMLInputElement>) {
        if (elementRef && !this.sliderMaxRef) {
            this.sliderMaxRef = elementRef;
        }
    }

    @ViewChild('sliderMin') set sliderMin(elementRef: ElementRef<HTMLInputElement>) {
        if (elementRef && !this.sliderMinRef) {
            this.sliderMinRef = elementRef;
        }
    }

    @Output() readonly valueChange = new EventEmitter<{ minValue?: number; maxValue?: number; value?: number }>();

    constructor(_vcr: ViewContainerRef, private readonly cdr: ChangeDetectorRef) { super(_vcr); }

    ngOnInit() {
        try {
            this.registerEvent('valueChange', this.valueChange);
            this.setSliderDisplay();
        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
        }
    }

    onMinMaxValueChange(isMinValue: boolean) {
        if (this.minValue != null && this.maxValue != null) {
            if (this.maxValue > this.max) {
                this.maxValue = this.max;
            }

            if (this.minValue < this.min) {
                this.minValue = this.min;
            }

            if (this.maxValue - this.minValue < this.gap) {
                if (isMinValue) {
                    this.minValue = this.maxValue - this.gap;
                    this.sliderMinRef.nativeElement.value = this.minValue.toString(); // Needed to enforce slider not moving past max
                } else {
                    this.maxValue = this.minValue + this.gap;
                    this.sliderMaxRef.nativeElement.value = this.maxValue.toString(); // Needed to enforce slider not moving past min
                }
            }

            this.cdr.detectChanges();

            this.setSliderDisplay();

            if (this.mode === SliderMode.Multiple) {
                this.valueChange.emit({ minValue: this.minValue, maxValue: this.maxValue });
            } else {
                this.valueChange.emit({ value: this.maxValue });
            }
        }
    }

    private setSliderDisplay() {
        if (this.sliderRef && this.max && this.minValue != null && this.maxValue != null) {
            this.sliderRef.nativeElement.style.left = this.mode === SliderMode.Multiple
                ? `${Math.max((this.minValue / this.max) * 100, 1)}%`
                : '0';
            this.sliderRef.nativeElement.style.right = `${Math.max(100 - (this.maxValue / this.max) * 100, 1)}%`;
        }
    }
}
